import { PropsWithChildren, useCallback, useRef, useEffect } from 'react'
import { clsx } from '@mantine/styles'
import { Box, Button, Typography } from '@percent/lemonade'
import styles from './rating-input.module.scss'
import { RatingInputProps } from './rating-input.types'

const Label = ({ children }: PropsWithChildren) => (
  <Typography variant="default" $color="gray400">
    {children}
  </Typography>
)

export function RatingInput({
  min = 1,
  max = 10,
  minLabel,
  maxLabel,
  value,
  onChange,
  onAfterChange,
  disabled = false,
  name,
  className,
}: Readonly<RatingInputProps>) {
  const numbers = Array.from({ length: max - min + 1 }, (_, i) => min + i)
  const afterChangeTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // This prevents React warnings about state updates on unmounted components
  useEffect(() => {
    return () => {
      if (afterChangeTimeoutRef.current) {
        clearTimeout(afterChangeTimeoutRef.current)
      }
    }
  }, [])

  const handleClick = useCallback(
    (num: number) => {
      if (disabled) {
        return
      }

      if (onChange) {
        // Toggle: if clicking the same value, deselect it
        onChange(value === num ? undefined : num)
      }
      // Clear any pending afterChange timeout from previous clicks
      if (afterChangeTimeoutRef.current) {
        clearTimeout(afterChangeTimeoutRef.current)
      }
      // Trigger onAfterChange after state update - execution to next event loop tick, allowing to update state first
      afterChangeTimeoutRef.current = setTimeout(() => {
        onAfterChange?.()
        afterChangeTimeoutRef.current = null
      }, 0)
    },
    [onChange, onAfterChange, value, disabled]
  )

  return (
    <Box
      className={clsx(
        styles.RatingInputWrapper,
        {
          [styles.Disabled]: disabled,
        },
        className
      )}
    >
      <Box className={styles.ButtonsWrapper}>
        {numbers.map((num) => (
          <Box key={num} className={styles.ButtonContainer}>
            <Button
              variant={value === num ? 'primary' : 'secondary'}
              shape="oval"
              size="small"
              stretch
              onClick={() => handleClick(num)}
              disabled={disabled && value !== num}
              data-testid={name ? `${name}-${num}` : undefined}
            >
              {num}
            </Button>
          </Box>
        ))}
      </Box>

      {(minLabel || maxLabel) && (
        <Box className={styles.Labels}>
          {minLabel && <Label key="min">{minLabel}</Label>}
          {maxLabel && <Label key="max">{maxLabel}</Label>}
        </Box>
      )}
    </Box>
  )
}
